import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  makeStyles
} from '@material-ui/core';
import React from 'react';

import DotSeparator from '../../../../components/DotSeparator';
import { DistrictListing } from '../../../../services/APIService';
import { formatCurrency } from '../../../../services/formatCurrency';

export interface DistrictResultProps {
  result: DistrictListing;
  onSelect: () => void;
}

const useStyles = makeStyles((theme) => ({
  member: {
    position: 'absolute',
    maxWidth: theme.spacing(4),
    top: theme.spacing(1),
    right: theme.spacing(1)
  }
}));

const DistrictResult: React.FC<DistrictResultProps> = ({
  result,
  onSelect
}) => {
  const classes = useStyles();

  const acres =
    result?.acreage &&
    `${result.acreage.toLocaleString(undefined, {
      maximumFractionDigits: 0
    })} Acres`;
  const subtitleLine = [result?.districtType, acres].filter((v) => v);

  const createdLine = result?.dateEstablished
    ? `Created ${new Date(result.dateEstablished).toLocaleDateString()}`
    : '';

  const bonds =
    result?.bondsOutstanding &&
    `${formatCurrency(result.bondsOutstanding)} Bonds`;
  const assessed =
    result?.assessedValue &&
    `${formatCurrency(result.assessedValue)} Assessed Value`;
  const valueLine = [bonds, assessed].filter((v) => v);

  const water =
    result?.waterConnections &&
    `${result.waterConnections.toLocaleString(undefined, {
      maximumFractionDigits: 0
    })} water`;
  const sanitary =
    result?.sanitaryConnections &&
    `${result.sanitaryConnections.toLocaleString(undefined, {
      maximumFractionDigits: 0
    })} sanitary`;
  const connectionItems = [water, sanitary].filter((v) => v).join(', ');
  const connectionsLine =
    connectionItems.length > 0 ? `Connections: ${connectionItems}` : '';

  return (
    <Card>
      <CardActionArea onClick={onSelect}>
        <CardContent style={{ flex: '1 0 auto' }}>
          <Box mb={0.5}>
            <Typography variant="subtitle1">{result?.name}</Typography>
          </Box>

          {subtitleLine.length > 0 && (
            <Box
              mb={0.5}
              mr={6}
              display="flex"
              flexDirection="row"
              flexWrap="wrap"
            >
              {subtitleLine.map((text, index) => (
                <React.Fragment key={text}>
                  {index > 0 && <DotSeparator color="primary" />}
                  <Typography variant="subtitle2" noWrap>
                    {text}
                  </Typography>
                </React.Fragment>
              ))}
            </Box>
          )}

          {createdLine.length > 0 && (
            <Typography variant="body2" gutterBottom>
              {createdLine}
            </Typography>
          )}

          {valueLine.length > 0 && (
            <Box display="flex" flexDirection="row" flexWrap="wrap">
              {valueLine.map((text, index) => (
                <React.Fragment key={text}>
                  {index > 0 && <DotSeparator color="action" />}
                  <Typography variant="body2" noWrap>
                    {text}
                  </Typography>
                </React.Fragment>
              ))}
            </Box>
          )}

          {connectionsLine.length > 0 && (
            <Typography variant="body2" noWrap>
              {connectionsLine}
            </Typography>
          )}

          {result?.isMember && (
            <img
              className={classes.member}
              src="https://awbd.org/wp-content/uploads/2021/04/AWBD-Main-logo_White_Blue2.png"
              alt="AWBD Member"
            />
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default DistrictResult;
